import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, CircularProgress, Alert } from '@mui/material';
import MainLayout from '../../../components/layout/MainLayout';
import { testApiEndpoint } from '../../../services/achievementService';

interface TestResults {
  status?: number;
  statusText: string;
  data: unknown;
}

function DebugAchievements() {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<TestResults | null>(null);
  const [apiUrl, setApiUrl] = useState<string>('');

  useEffect(() => {
    // Get the API URL from environment or default
    const envApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    setApiUrl(envApiUrl);
  }, []);

  const testEndpoint = async (endpoint: string) => {
    setLoading(true);
    setError(null);
    setResults(null);
    
    try {
      const result = await testApiEndpoint(endpoint);
      
      if (result.success) {
        setResults({
          status: result.status,
          statusText: 'OK',
          data: result.data
        });
      } else {
        console.error('API Test Error:', result.error);
        setError(`Error ${result.status || 'unknown'}: ${result.error}`);
        
        setResults({
          status: result.status,
          statusText: 'Error',
          data: result.data
        });
      }
    } catch (err: unknown) {
      console.error('Unexpected error:', err);
      const error = err as { message?: string };
      setError(`Unexpected error: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <Typography variant="h4" component="h1" gutterBottom>
        API Debug Page
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1">
          Current API URL: <code>{apiUrl}</code>
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={() => testEndpoint('/achievements')}>
          Test /achievements
        </Button>
        <Button variant="contained" onClick={() => testEndpoint('/achievements/1')}>
          Test /achievements/1
        </Button>
        <Button variant="contained" onClick={() => testEndpoint('/auth/me')}>
          Test /auth/me
        </Button>
        <Button variant="contained" onClick={() => testEndpoint('/users')}>
          Test /users
        </Button>
        <Button variant="contained" onClick={() => testEndpoint('/health')}>
          Test /health
        </Button>
      </Box>
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {results && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Response Details:
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Status: {results.status} {results.statusText}</Typography>
          </Box>
          
          <Typography variant="subtitle1" gutterBottom>Data:</Typography>
          <Box 
            component="pre" 
            sx={{ 
              bgcolor: '#f5f5f5', 
              p: 2, 
              borderRadius: 1, 
              overflow: 'auto',
              maxHeight: '400px'
            }}
          >
            {JSON.stringify(results.data, null, 2)}
          </Box>
        </Paper>
      )}
    </MainLayout>
  );
}

export default DebugAchievements;